/* eslint-disable react-hooks/exhaustive-deps */
'use client';

import type { DefaultQueryError, User } from '@ahm/api-wrappers-core';
import { decodeToken } from '@ahm/common-helpers';
import type { QueryObserverResult } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useGetUserProfile } from '../api';
import {
  type SupplierProfile,
  useGetSupplierProfile,
} from '../api/use-get-supplier-profile';
import { useAuth } from '../auth-provider';
import { AuthStatus } from '../lib/constants';
import { usePathname, useSearchParams } from '../lib/navigation';

export type UseMyProfileProps = {
  withWebinapp?: boolean; // Optional prop, default is false
};

export type UseMyProfile = {
  user?: User;
  supplierProfile?: SupplierProfile;
  isLoading: boolean;
  isFetching: boolean;
  isFetched: boolean;
  refetch?: () => Promise<
    QueryObserverResult<User | SupplierProfile, DefaultQueryError>
  >;
};

export function useMyProfile({
  withWebinapp = false,
}: UseMyProfileProps = {}): UseMyProfile {
  const { status, session, loading, webInAppConfig } = useAuth();
  const searchParams = useSearchParams();
  const pathname = usePathname();

  const tokenQueryParam = searchParams.get('token');
  const webinappQueryParam = searchParams.get('webinapp');

  const accessToken = useMemo(() => session?.token ?? null, [session]);

  const decodeTokenData = useMemo(() => {
    return accessToken ? decodeToken(accessToken) : null;
  }, [accessToken]);

  const isWebinappValid = useMemo(() => {
    if (!withWebinapp || !webinappQueryParam) return true; // Skip validation if not required or no query param

    return webInAppConfig?.id === webinappQueryParam;
  }, [withWebinapp, webinappQueryParam, webInAppConfig]);

  const isUserEnabled = useMemo(() => {
    return (
      status === AuthStatus.Authenticated &&
      decodeTokenData?.typ === 'user' &&
      !pathname.includes('supplier') &&
      (!tokenQueryParam || tokenQueryParam === accessToken) &&
      isWebinappValid
    );
  }, [
    status,
    decodeTokenData,
    pathname,
    tokenQueryParam,
    accessToken,
    isWebinappValid,
  ]);

  const isSupplierEnabled = useMemo(() => {
    return (
      status === AuthStatus.Authenticated &&
      decodeTokenData?.typ === 'supplier' &&
      pathname.includes('supplier') &&
      (!tokenQueryParam || tokenQueryParam === accessToken) &&
      isWebinappValid
    );
  }, [
    status,
    decodeTokenData,
    pathname,
    tokenQueryParam,
    accessToken,
    isWebinappValid,
  ]);

  const {
    data: user,
    isFetching: isFetchingUser,
    isFetched: isFetchedUser,
    refetch: refetchUser,
  } = useGetUserProfile({
    enabled: isUserEnabled,
  });

  const {
    data: supplierProfile,
    isFetching: isFetchingSupplier,
    isFetched: isFetchedSupplier,
    refetch: refetchSupplier,
  } = useGetSupplierProfile({
    enabled: isSupplierEnabled,
  });

  return useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    const isLoading = loading || isFetchingUser || isFetchingSupplier;
    const isFetched = isFetchedUser || isFetchedSupplier;
    const isFetching = isFetchingUser || isFetchingSupplier;

    return {
      user,
      supplierProfile,
      isLoading,
      isFetching,
      isFetched,
      refetch: isUserEnabled
        ? refetchUser
        : isSupplierEnabled
          ? refetchSupplier
          : undefined,
    };
  }, [
    user,
    supplierProfile,
    loading,
    isFetchingUser,
    isFetchingSupplier,
    isFetchedUser,
    isFetchedSupplier,
  ]);
}
